import React, { useState, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import { toast, ToastContainer } from "react-toastify";

const TwoFAModal = ({ open, onClose, location }) => {
  const [serviceType, setServiceType] = useState("");
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [captureLoading, setCaptureLoading] = useState(false);
  const [deviceDiscovered, setDeviceDiscovered] = useState(false);

  const finalUrlRef = useRef(null);

  const discoverAvdm = async () => {
    setDiscoverLoading(true);
    let successFlag = false;
    let primaryUrl =
      window.location.protocol === "https:" ? "https://127.0.0.1:" : "http://127.0.0.1:";

    for (let i = 11100; i <= 11120; i++) {
      try {
        const response = await fetch(primaryUrl + i, {
          method: "RDSERVICE",
          mode: "cors",
          headers: { "Content-Type": "text/xml; charset=utf-8" },
        });

        if (!response.ok) continue;
        const data = await response.text();
        if (data.includes("Mantra")) {
          finalUrlRef.current = primaryUrl + i;
          successFlag = true;
          break;
        }
      } catch (error) {
        console.error("Error discovering AVDM:", error);
      }
    }

    setDiscoverLoading(false);
    if (successFlag) {
      setDeviceDiscovered(true);
      toast.success("Device discovered successfully!");
    } else {
      toast.error("Device discovery failed. Please try again.");
    }
  };

  const captureAvdm = async () => {
    if (!finalUrlRef.current) {
      alert("Please discover AVDM first.");
      return;
    }

    const captureUrl = `${finalUrlRef.current}/rd/capture`;

    const XML = `<?xml version="1.0"?>
        <PidOptions ver="1.0">
            <Opts fCount="1" fType="2" iCount="0" pCount="0" format="0" pidVer="2.0"  timeout="10000"  posh="UNKNOWN" env="P" />
            <CustOpts>
                <Param name="mantrakey" value="SOME_KEY_VALUE" />
            </CustOpts>
        </PidOptions>`;

    try {
      setCaptureLoading(true);
      const response = await fetch(captureUrl, {
        method: "CAPTURE",
        headers: {
          "Content-Type": "text/xml; charset=utf-8",
        },
        body: XML,
      });

      const data = await response.text();
      const xmlDoc = new DOMParser().parseFromString(data, "text/xml");

      const resp = xmlDoc.getElementsByTagName("Resp")[0];
      const errInfo = resp.getAttribute("errInfo");

      const deviceInfo = xmlDoc.getElementsByTagName("DeviceInfo")[0];
      const additionalInfo = deviceInfo.getElementsByTagName("additional_info")[0];
      const skey = xmlDoc.getElementsByTagName("Skey")[0];
      const pidDataElement = xmlDoc.getElementsByTagName("Data")[0];

      const skeydata = skey.textContent;
      const ci = skey.getAttribute("ci");
      const piddata = pidDataElement.textContent;
      const pidtype = pidDataElement.getAttribute("type");

      const parameters = [];
      const params = additionalInfo.getElementsByTagName("Param");
      for (let i = 0; i < params.length; i++) {
        parameters.push({
          name: params[i].getAttribute("name"),
          value: params[i].getAttribute("value"),
        });
      }

      const extractedData = {
        resp: {
          errCode: resp.getAttribute("errCode"),
          errInfo: errInfo,
          fCount: resp.getAttribute("fCount"),
          fType: resp.getAttribute("fType"),
          nmPoints: resp.getAttribute("nmPoints"),
          qScore: resp.getAttribute("qScore"),
        },
        deviceInfo: {
          dpId: deviceInfo.getAttribute("dpId"),
          rdsId: deviceInfo.getAttribute("rdsId"),
          rdsVer: deviceInfo.getAttribute("rdsVer"),
          mi: deviceInfo.getAttribute("mi"),
          mc: deviceInfo.getAttribute("mc"),
          dc: deviceInfo.getAttribute("dc"),
        },
        additionalInfo: parameters,
        ci: ci,
        skeydata: skeydata,
        hmac: xmlDoc.getElementsByTagName("Hmac")[0].textContent,
        piddata: piddata,
        pidtype: pidtype,
      };

      if (errInfo === "Success.") {
        const combinedPayload = {
          ...extractedData,
          serviceType,
          latitude: location.latitude,
          longitude: location.longitude,
        };

        await call2faApi(combinedPayload);
      } else {
        toast.error(`Capture failed: ${errInfo}`);
      }

    } catch (error) {
      console.error("Error capturing data:", error);
      alert("Failed to capture data. Please try again.");
    } finally {
      setCaptureLoading(false);
    }
  };

  const call2faApi = async (payload) => {
    try {
      const token = sessionStorage.getItem("jwtToken");
      if (!token) {
        toast.error("Authentication token missing.");
        return;
      }

      const response = await fetch("https://api.ipaisa.co.in/myapp/cms/tfa", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage =
          data?.body?.message ||
          data?.message ||
          `Error: ${response.status}`;
        toast.error(errorMessage);
        console.error("2FA error:", data);
        return;
      }

      const successMessage =
        data?.body?.message ||
        data?.message ||
        "2FA authentication successful!";
      toast.success(successMessage);
      console.log("2FA API response:", data);
    } catch (error) {
      toast.error(error?.message || "2FA submission failed.");
      console.error("2FA error:", error);
    }
  };


  return (
    <Modal open={open} onClose={onClose}>
      <Box
        component={motion.div}
        initial={{ y: "100%", opacity: 0 }}
        animate={{ y: "0%", opacity: 1 }}
        exit={{ y: "100%", opacity: 0 }}
        transition={{ type: "spring", stiffness: 120, damping: 15 }}
        sx={{
          position: "absolute",
          bottom: 350,
          left: "35%",
          transform: "translate(-50%, 0)",
          width: 420,
          bgcolor: "background.paper",
          boxShadow: 6,
          p: 3,
          borderRadius: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h6">Two-Factor Authentication</Typography>

        <Select
          value={serviceType}
          onChange={(e) => setServiceType(e.target.value)}
          displayEmpty
          fullWidth
          sx={{ mt: 2, mb: 2 }}
        >
          <MenuItem value="" disabled>Select Service Type</MenuItem>
          <MenuItem value="AEPS">AEPS</MenuItem>
          <MenuItem value="AP">AP</MenuItem>
          <MenuItem value="CD">CD</MenuItem>
        </Select>

        <Box display="flex" gap={2} justifyContent="center">
          <Button
            variant="contained"
            onClick={discoverAvdm}
            disabled={discoverLoading}
          >
            {discoverLoading ? <CircularProgress size={24} /> : "Discover"}
          </Button>

          <Button
            variant="contained"
            onClick={captureAvdm}
            disabled={!deviceDiscovered || captureLoading}
          >
            {captureLoading ? <CircularProgress size={24} /> : "Capture"}
          </Button>
        </Box>

        <ToastContainer />
      </Box>
    </Modal>
  );
};

export default TwoFAModal;
